// ResetPassword.tsx
import React, { useMemo, useState } from 'react';
import {
  Video,
  Lock,
  Eye,
  EyeOff,
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  AlertTriangle,
  ShieldCheck,
} from 'lucide-react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { authService } from '../services/auth.service.ts';

interface PasswordStrength {
  score: 0 | 1 | 2 | 3 | 4;
  label: string;
  color: string;
}

const evaluateStrength = (pwd: string): PasswordStrength => {
  let score = 0;
  if (pwd.length >= 8) score++;
  if (/[A-Z]/.test(pwd) && /[a-z]/.test(pwd)) score++;
  if (/\d/.test(pwd)) score++;
  if (/[^A-Za-z0-9]/.test(pwd)) score++;

  const map: Record<number, PasswordStrength> = {
    0: { score: 0, label: 'Çok zayıf', color: 'bg-red-500' },
    1: { score: 1, label: 'Zayıf', color: 'bg-red-400' },
    2: { score: 2, label: 'Orta', color: 'bg-yellow-500' },
    3: { score: 3, label: 'İyi', color: 'bg-blue-500' },
    4: { score: 4, label: 'Güçlü', color: 'bg-green-500' },
  };
  return map[score];
};

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const params = useParams<{ token?: string }>();
  const [searchParams] = useSearchParams();

  // Token hem /reset-password/:token hem de /reset-password?token=... formundan okunabilir
  const token = params.token || searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  const strength = useMemo(() => evaluateStrength(password), [password]);

  const validate = (): string | null => {
    if (!token) return 'Geçersiz ya da eksik sıfırlama bağlantısı.';
    if (password.length < 8) return 'Şifre en az 8 karakter olmalıdır.';
    if (!/[A-Za-z]/.test(password) || !/\d/.test(password)) {
      return 'Şifre en az bir harf ve bir rakam içermelidir.';
    }
    if (password !== confirmPassword) return 'Şifreler eşleşmiyor.';
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsLoading(true);
    try {
      await authService.resetPassword(token, password);
      setIsSuccess(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 400 || status === 401 || status === 410) {
        setError(
          'Bu sıfırlama bağlantısı geçersiz veya süresi dolmuş. Lütfen yeni bir bağlantı talep edin.'
        );
      } else if (status === 429) {
        setError('Çok fazla deneme yapıldı. Lütfen bir süre sonra tekrar deneyin.');
      } else {
        setError(
          err?.response?.data?.error ||
            err?.message ||
            'Şifre sıfırlanamadı. Lütfen tekrar deneyin.'
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center px-4 overflow-hidden">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute w-96 h-96 bg-purple-500/30 rounded-full blur-3xl -top-48 -left-48 animate-pulse"></div>
        <div className="absolute w-96 h-96 bg-blue-500/30 rounded-full blur-3xl -bottom-48 -right-48 animate-pulse"></div>
      </div>

      <div className="relative w-full max-w-md">
        <div className="absolute -inset-1 bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl blur opacity-30"></div>
        <div className="relative bg-slate-800/80 backdrop-blur-xl rounded-2xl border border-white/10 p-6">
          <Link
            to="/login"
            className="inline-flex items-center space-x-2 text-gray-400 hover:text-white transition mb-4 group"
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition" />
            <span>Giriş Sayfasına Dön</span>
          </Link>

          <div className="flex items-center justify-center space-x-2 mb-4">
            <Video className="w-8 h-8 text-purple-400" />
            <span className="text-2xl font-bold text-white">TBKMeet</span>
          </div>

          {!token ? (
            <div className="text-center space-y-4 py-4">
              <div className="flex justify-center">
                <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-10 h-10 text-red-400" />
                </div>
              </div>
              <h2 className="text-xl font-bold text-white">Geçersiz Bağlantı</h2>
              <p className="text-gray-400 text-sm">
                Sıfırlama bağlantısı eksik veya geçersiz. Lütfen şifre sıfırlama
                e-postasındaki bağlantıyı kullanın.
              </p>
              <Link
                to="/forgot-password"
                className="inline-flex w-full py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-semibold transition transform hover:scale-105 hover:shadow-lg hover:shadow-purple-500/25 items-center justify-center space-x-2"
              >
                <span>Yeni Bağlantı Talep Et</span>
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          ) : isSuccess ? (
            <div className="text-center space-y-4 py-2">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-10 h-10 text-green-400" />
                </div>
              </div>
              <h2 className="text-xl font-bold text-white">Şifreniz Güncellendi!</h2>
              <p className="text-gray-400 text-sm">
                Yeni şifrenizle giriş yapabilirsiniz. Birkaç saniye içinde giriş sayfasına
                yönlendirileceksiniz.
              </p>
              <Link
                to="/login"
                className="inline-flex w-full py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-semibold transition transform hover:scale-105 hover:shadow-lg hover:shadow-purple-500/25 items-center justify-center space-x-2"
              >
                <span>Giriş Yap</span>
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          ) : (
            <>
              <h2 className="text-xl font-bold text-white text-center mb-1">
                Yeni Şifre Belirle
              </h2>
              <p className="text-gray-400 text-center text-sm mb-6">
                Hesabınız için güçlü bir şifre oluşturun
              </p>

              {error && (
                <div
                  role="alert"
                  className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-300 text-sm"
                >
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Yeni Şifre
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      autoComplete="new-password"
                      className="w-full pl-10 pr-10 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition text-sm"
                      required
                      minLength={8}
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition"
                      aria-label={showPassword ? 'Şifreyi gizle' : 'Şifreyi göster'}
                    >
                      {showPassword ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  </div>

                  {password && (
                    <div className="mt-2">
                      <div className="flex gap-1 mb-1">
                        {[0, 1, 2, 3].map((i) => (
                          <div
                            key={i}
                            className={`h-1 flex-1 rounded-full transition-colors ${
                              i < strength.score ? strength.color : 'bg-white/10'
                            }`}
                          />
                        ))}
                      </div>
                      <p className="text-xs text-gray-400 flex items-center gap-1">
                        <ShieldCheck className="w-3 h-3" />
                        <span>Şifre gücü: {strength.label}</span>
                      </p>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Yeni Şifre (Tekrar)
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="••••••••"
                      autoComplete="new-password"
                      className="w-full pl-10 pr-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition text-sm"
                      required
                      minLength={8}
                      disabled={isLoading}
                    />
                  </div>
                  {confirmPassword && password !== confirmPassword && (
                    <p className="mt-1 text-xs text-red-400">Şifreler eşleşmiyor</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isLoading || !password || !confirmPassword}
                  className="w-full py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-semibold transition transform hover:scale-105 hover:shadow-lg hover:shadow-purple-500/25 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {isLoading ? (
                    <>
                      <svg
                        className="animate-spin h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      <span>Şifre Sıfırlanıyor...</span>
                    </>
                  ) : (
                    <>
                      <span>Şifreyi Güncelle</span>
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </button>
              </form>
            </>
          )}

          <div className="mt-6 pt-4 border-t border-white/10">
            <p className="text-center text-gray-400 text-sm">
              Hesabınız yok mu?{' '}
              <Link
                to="/register"
                className="text-purple-400 hover:text-purple-300 font-semibold transition"
              >
                Kayıt Ol
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
